import { CloudRain, Sun, CloudSnow, Cloud, Snowflake, Droplets, Wind, AlertTriangle } from "lucide-react";

const codeIcon = (code) => {
    if (code === 0 || code === 1) return Sun;
    if ([45, 48].includes(code)) return Cloud;
    if ([51, 53, 55, 61, 63, 65, 80, 81, 82].includes(code)) return CloudRain;
    if ([71, 73, 75].includes(code)) return CloudSnow;
    if (code === 95 || code === 96 || code === 99) return CloudRain;
    return Cloud;
};

const weekday = (dateStr) => {
    try {
        return new Date(dateStr).toLocaleDateString("en-IE", { weekday: "short" });
    } catch {
        return dateStr;
    }
};

export default function WeatherStrip({ weather }) {
    if (!weather) {
        return (
            <div className="journal-card p-6 text-sm text-[var(--vm-text-muted)]" data-testid="weather-strip-empty">
                Loading weather…
            </div>
        );
    }
    const days = (weather.days || []).slice(0, 7);
    const alerts = weather.alerts || [];

    return (
        <div className="journal-card p-6" data-testid="weather-strip">
            <div className="flex items-center justify-between mb-4">
                <div>
                    <div className="overline text-[var(--vm-sage)] text-xs uppercase tracking-[0.25em] font-semibold">7-day forecast</div>
                    <div className="font-serif-display text-2xl mt-1 text-[var(--vm-text)]">Weather Watch</div>
                </div>
                {weather.current && (
                    <div className="text-right">
                        <div className="text-3xl font-serif-display text-[var(--vm-accent)]">{Math.round(weather.current.temperature_2m)}°</div>
                        <div className="text-xs text-[var(--vm-text-muted)] flex items-center gap-2 justify-end">
                            <Droplets className="w-3 h-3" /> {weather.current.relative_humidity_2m}%
                            <Wind className="w-3 h-3 ml-2" /> {Math.round(weather.current.wind_speed_10m)} km/h
                        </div>
                    </div>
                )}
            </div>

            <div className="grid grid-cols-7 gap-2">
                {days.map((d) => {
                    const Icon = codeIcon(d.weather_code);
                    return (
                        <div key={d.date} className="text-center px-1 py-3 rounded-lg bg-[var(--vm-input)]/60" data-testid={`weather-day-${d.date}`}>
                            <div className="text-xs uppercase tracking-wider text-[var(--vm-text-muted)]">{weekday(d.date)}</div>
                            <Icon className="w-5 h-5 mx-auto my-2 text-[var(--vm-accent)]" strokeWidth={1.5} />
                            <div className="text-sm font-medium text-[var(--vm-text)]">{Math.round(d.temp_max)}°</div>
                            <div className="text-xs text-[var(--vm-text-muted)]">{Math.round(d.temp_min)}°</div>
                            {d.precipitation_sum > 0 && (
                                <div className="text-[10px] text-[var(--vm-secondary)] mt-1">{d.precipitation_sum.toFixed(1)}mm</div>
                            )}
                        </div>
                    );
                })}
            </div>

            {alerts.length > 0 && (
                <div className="mt-5 space-y-2" data-testid="weather-alerts">
                    {alerts.slice(0, 3).map((a) => (
                        <div key={`${a.type}-${a.message}`} className="flex items-start gap-3 p-3 rounded-lg bg-[#F1D7C7]/40 border border-[var(--vm-secondary)]/30">
                            {a.type === "frost" ? (
                                <Snowflake className="w-4 h-4 text-[var(--vm-secondary)] mt-0.5 shrink-0" strokeWidth={1.5} />
                            ) : (
                                <AlertTriangle className="w-4 h-4 text-[var(--vm-secondary)] mt-0.5 shrink-0" strokeWidth={1.5} />
                            )}
                            <div className="text-sm text-[var(--vm-text)]">{a.message}</div>
                        </div>
                    ))}
                </div>
            )}
        </div>
    );
}
